import { AbsoluteHref } from '@weenat/client/dist/routx/runtime-core'
import { useIntl } from '@weenat/wintl'
import { IconComponent, IconProps } from 'app/src/kit/Icon'
import Icons from 'app/src/kit/Icons'
import SidePanelLink from 'app/src/kit/SidePanelLink'
import Text from 'app/src/kit/Text'
import PanelLinkSection from 'app/src/kit/panels/PanelLinkSection'
import TextTooltip from 'app/src/kit/tooltips/TextTooltip'
import { useFavorites, useToggleFeature } from 'app/state'
import { isNil } from 'lodash-es'
import { MouseEvent, useMemo } from 'react'
import { styled } from 'styled-components'
import { useFavoriteForPath } from './useFavoriteForPath'

const FavoriteItemContainer = styled(Flex)`
  position: relative;

  .remove-btn {
    opacity: 0;
    transition: opacity 0.1s;
  }

  &:hover .remove-btn {
    opacity: 1;
  }
`

const RemoveButton = styled.div`
  position: absolute;
  right: 4px;
  top: 50%;
  transform: translateY(-50%);
  z-index: 1;
  display: flex;
  align-items: center;
  justify-content: center;
`

interface FavoriteItemProps {
  name: string
  icon: string
  path: string
}

function extractLeadingEmoji(name: string): string | null {
  const match = name.match(/^\p{Emoji_Presentation}/u)
  return match ? match[0] : null
}

const FavoriteItem: FC<FavoriteItemProps> = ({ name, icon, path }) => {
  const { t } = useIntl()
  const { removeFavorite } = useFavoriteForPath(path)
  const emoji = extractLeadingEmoji(name)
  const LeftIcon = useMemo(() => {
    if (!isNil(emoji)) {
      const EmojiIcon: FC<IconProps> = () => <Text $fontSize='rg'>{emoji}</Text>
      return EmojiIcon
    }
    return (Icons as Record<string, IconComponent>)[icon] ?? Icons.Star
  }, [emoji, icon])

  const handleRemove = (e: MouseEvent) => {
    e.preventDefault()
    e.stopPropagation()
    removeFavorite()
  }

  return (
    <FavoriteItemContainer>
      <Box $flex={1} $minWidth={0}>
        <SidePanelLink
          LeftIcon={LeftIcon}
          label={!isNil(emoji) ? name.slice(emoji.length).trimStart() : name}
          to={path as AbsoluteHref}
          matchExactPath={false}
        />
      </Box>
      <RemoveButton className='remove-btn'>
        <TextTooltip restMs={500} content={t('actions.remove_from_favorite')}>
          <Icons.Bin $size='md' $p='sm' $color='grayscale.700' onPress={handleRemove} />
        </TextTooltip>
      </RemoveButton>
    </FavoriteItemContainer>
  )
}

const DelimitedPanelLinkSection = styled(PanelLinkSection)`
  border-bottom: 1px solid ${(p) => p.theme.colors.grayscale[100]};
  margin-bottom: ${(p) => p.theme.spacings[400]}px;
`

const FavoritesNavSection: FC = () => {
  const [{ on_nav_favorites }] = useToggleFeature()
  const [favorites] = useFavorites()
  const { t } = useIntl()

  if (!on_nav_favorites || favorites.length === 0) return null

  return (
    <DelimitedPanelLinkSection title={t('models.favorite.model.name')}>
      {favorites.map((fav) => (
        <FavoriteItem key={fav.path} name={fav.name} icon={fav.icon} path={fav.path} />
      ))}
    </DelimitedPanelLinkSection>
  )
}

export default FavoritesNavSection
